import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Text, Area, Toggle, SingleImageEditor, inputCls } from "@/components/admin/AdminUI";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function AdminContent() {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p>Loading…</p>;

  const up = (path, value) => {
    setS((prev) => {
      const next = structuredClone(prev);
      let o = next;
      for (let i = 0; i < path.length - 1; i++) { o[path[i]] = o[path[i]] ?? {}; o = o[path[i]]; }
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved — live on the site"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const hp = s.homepage || {}, about = s.about || {}, contact = s.contact || {}, social = s.social || {}, policies = s.policies || {}, seo = s.seo || {};
  const faq = s.faq || [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-kys-ink">Content & Settings</h1>
        <button data-testid="save-settings" onClick={save} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-kys-accent"><Save className="h-4 w-4" /> Save All</button>
      </div>

      <Tabs defaultValue="brand" className="mt-5">
        <TabsList className="flex-wrap">
          <TabsTrigger value="brand">Brand</TabsTrigger>
          <TabsTrigger value="home">Homepage</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
          <TabsTrigger value="contact">Contact & Social</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="config">Payments/Tax/Ship</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="space-y-4">
          <Text label="Brand name" value={s.brand_name} onChange={(v) => up(["brand_name"], v)} />
          <Text label="Legal name" value={s.legal_name} onChange={(v) => up(["legal_name"], v)} />
          <Text label="Tagline" value={s.tagline} onChange={(v) => up(["tagline"], v)} />
        </TabsContent>

        <TabsContent value="home" className="space-y-4">
          <Text label="Hero title" value={hp.hero_title} onChange={(v) => up(["homepage", "hero_title"], v)} />
          <Area label="Hero subtitle" value={hp.hero_subtitle} onChange={(v) => up(["homepage", "hero_subtitle"], v)} rows={2} />
          <SingleImageEditor label="Hero image" value={typeof hp.hero_image === "string" ? { url: hp.hero_image } : hp.hero_image} onChange={(v) => up(["homepage", "hero_image"], v?.url || "")} />
          <Area label="Handmade blurb" value={hp.handmade_blurb} onChange={(v) => up(["homepage", "handmade_blurb"], v)} rows={2} />
          <Area label="Custom order blurb" value={hp.custom_blurb} onChange={(v) => up(["homepage", "custom_blurb"], v)} rows={2} />
          <div className="grid grid-cols-2 gap-4">
            <Text label="Instagram handle" value={hp.instagram_handle} onChange={(v) => up(["homepage", "instagram_handle"], v)} />
            <Text label="Instagram URL" value={hp.instagram_url} onChange={(v) => up(["homepage", "instagram_url"], v)} />
          </div>
        </TabsContent>

        <TabsContent value="about" className="space-y-4">
          <Text label="Heading" value={about.heading} onChange={(v) => up(["about", "heading"], v)} />
          <Area label="Story" value={about.body} onChange={(v) => up(["about", "body"], v)} rows={6} />
          <SingleImageEditor label="About image" value={typeof about.image === "string" ? { url: about.image } : about.image} onChange={(v) => up(["about", "image"], v?.url || "")} />
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <Text label="Public email" value={contact.email} onChange={(v) => up(["contact", "email"], v)} />
          <Text label="Public phone" value={contact.phone} onChange={(v) => up(["contact", "phone"], v)} />
          <Text label="Location" value={contact.location} onChange={(v) => up(["contact", "location"], v)} />
          <Area label="Contact note" value={contact.note} onChange={(v) => up(["contact", "note"], v)} rows={2} />
          <div className="grid grid-cols-2 gap-4">
            <Text label="Instagram" value={social.instagram} onChange={(v) => up(["social", "instagram"], v)} />
            <Text label="TikTok" value={social.tiktok} onChange={(v) => up(["social", "tiktok"], v)} />
            <Text label="Facebook" value={social.facebook} onChange={(v) => up(["social", "facebook"], v)} />
            <Text label="Twitter / X" value={social.twitter} onChange={(v) => up(["social", "twitter"], v)} />
          </div>
        </TabsContent>

        <TabsContent value="faq" className="space-y-4">
          {faq.map((item, i) => (
            <div key={i} className="rounded border border-kys-blush-border p-3">
              <input className={inputCls} value={item.q} onChange={(e) => up(["faq", i, "q"], e.target.value)} placeholder="Question" />
              <textarea className={`${inputCls} mt-2`} rows={2} value={item.a} onChange={(e) => up(["faq", i, "a"], e.target.value)} placeholder="Answer" />
              <button onClick={() => up(["faq"], faq.filter((_, idx) => idx !== i))} className="mt-2 flex items-center gap-1 text-xs text-destructive"><Trash2 className="h-3 w-3" /> Remove</button>
            </div>
          ))}
          <button data-testid="add-faq" onClick={() => up(["faq"], [...faq, { q: "", a: "" }])} className="flex items-center gap-1.5 rounded-full border border-kys-blush-border px-4 py-2 text-sm font-medium text-kys-charcoal hover:border-kys-accent"><Plus className="h-4 w-4" /> Add FAQ</button>
        </TabsContent>

        <TabsContent value="policies" className="space-y-4">
          {[["privacy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["shipping", "Shipping Policy"], ["returns", "Returns & Exchange"], ["custom_order", "Custom Order Policy"], ["cancellation", "Cancellation Policy"]].map(([k, label]) => (
            <Area key={k} label={label} value={policies[k]} onChange={(v) => up(["policies", k], v)} rows={4} />
          ))}
        </TabsContent>

        <TabsContent value="config" className="space-y-6">
          <div className="rounded border border-kys-blush-border p-4">
            <p className="mb-3 font-medium text-kys-ink">Payments</p>
            <Toggle label="Payments configured (Stripe connected)" checked={s.payments?.configured} onChange={(v) => up(["payments", "configured"], v)} />
            <Area label="Note" value={s.payments?.note} onChange={(v) => up(["payments", "note"], v)} rows={2} />
          </div>
          <div className="rounded border border-kys-blush-border p-4">
            <p className="mb-2 font-medium text-kys-ink">Sales Tax</p>
            <div className="flex items-start gap-2 rounded bg-kys-blush px-3 py-3 text-sm text-kys-charcoal">
              <span className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full bg-green-500" />
              <div>
                <p className="font-medium text-kys-ink">Automatic sales tax (Stripe Tax)</p>
                <p className="mt-1 text-xs text-kys-muted">Sales tax is calculated automatically at checkout based on the customer's U.S. shipping address, using your Stripe Tax settings and registrations. Products are taxed as Clothing &amp; Footwear. There's no manual rate to set here — manage registrations and origin in your Stripe Dashboard.</p>
              </div>
            </div>
          </div>
          <div className="rounded border border-kys-blush-border p-4">
            <p className="mb-3 font-medium text-kys-ink">Shipping</p>
            <Toggle label="U.S. only" checked={s.shipping?.us_only} onChange={(v) => up(["shipping", "us_only"], v)} />
            <Area label="Shipping note" value={s.shipping?.note} onChange={(v) => up(["shipping", "note"], v)} rows={2} />

            <p className="mb-2 mt-5 text-sm font-medium text-kys-ink">Ship-From Address <span className="font-normal text-kys-muted">(used for live Shippo rates & labels)</span></p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Text label="Name / Business" value={s.shipping?.origin?.name} onChange={(v) => up(["shipping", "origin", "name"], v)} testid="ship-origin-name" />
              <Text label="Phone" value={s.shipping?.origin?.phone} onChange={(v) => up(["shipping", "origin", "phone"], v)} testid="ship-origin-phone" />
              <Text label="Street address" value={s.shipping?.origin?.street1} onChange={(v) => up(["shipping", "origin", "street1"], v)} testid="ship-origin-street1" />
              <Text label="Apt / Suite (optional)" value={s.shipping?.origin?.street2} onChange={(v) => up(["shipping", "origin", "street2"], v)} />
              <Text label="City" value={s.shipping?.origin?.city} onChange={(v) => up(["shipping", "origin", "city"], v)} testid="ship-origin-city" />
              <Text label="State (2-letter)" value={s.shipping?.origin?.state} onChange={(v) => up(["shipping", "origin", "state"], v)} testid="ship-origin-state" />
              <Text label="ZIP code" value={s.shipping?.origin?.zip} onChange={(v) => up(["shipping", "origin", "zip"], v)} testid="ship-origin-zip" />
            </div>

            <p className="mb-1 mt-5 text-sm font-medium text-kys-ink">Package Presets</p>
            <p className="mb-3 text-[11px] text-kys-muted">Dimensions in inches, weights in ounces. The system auto-picks the smallest enabled package whose “Max weight it can hold” fits the order's total item weight.</p>
            <div className="space-y-3">
              {(s.shipping?.packages || []).map((pkg, i) => (
                <div key={pkg.id || i} data-testid={`ship-pkg-${pkg.id || i}`} className="rounded border border-kys-blush-border bg-kys-cream/40 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-kys-ink">{pkg.name}</span>
                    <Toggle label="Enabled" checked={pkg.enabled} onChange={(v) => up(["shipping", "packages", i, "enabled"], v)} testid={`ship-pkg-enabled-${pkg.id || i}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <Text label="Length (in)" type="number" value={pkg.length} onChange={(v) => up(["shipping", "packages", i, "length"], v)} testid={`ship-pkg-length-${pkg.id || i}`} />
                    <Text label="Width (in)" type="number" value={pkg.width} onChange={(v) => up(["shipping", "packages", i, "width"], v)} />
                    <Text label="Height (in)" type="number" value={pkg.height} onChange={(v) => up(["shipping", "packages", i, "height"], v)} />
                    <Text label="Empty weight (oz)" type="number" value={pkg.empty_weight_oz} onChange={(v) => up(["shipping", "packages", i, "empty_weight_oz"], v)} />
                    <Text label="Max weight (oz)" type="number" value={pkg.max_weight_oz} onChange={(v) => up(["shipping", "packages", i, "max_weight_oz"], v)} testid={`ship-pkg-maxweight-${pkg.id || i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="space-y-4">
          <Text label="Page title" value={seo.title} onChange={(v) => up(["seo", "title"], v)} />
          <Area label="Meta description" value={seo.description} onChange={(v) => up(["seo", "description"], v)} rows={2} />
        </TabsContent>

        <TabsContent value="account" className="space-y-4">
          <p className="text-sm text-kys-muted">Change your owner login password.</p>
          <Text label="Current password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} />
          <Text label="New password (min 8 chars)" type="password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} />
          <button data-testid="change-password" onClick={changePw} className="rounded-full bg-kys-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-kys-accent">Change Password</button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
